import { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { getFlightsTable } from "@/api/flights";
import { FilterBar, type FilterSpec } from "@/components/FilterBar";
import { FlightTable } from "@/components/FlightTable";
import { useAuth } from "@/auth/AuthProvider";
import { canReviewAllFlights } from "@/auth/roles";
import { PageHeader } from "@/components/PageHeader";
import { Pagination } from "@/components/Pagination";
import { useDelayedFlag, usePulseOnChange } from "@/hooks/useDelayedFlag";
import { formatDuration } from "@/lib/format";

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "duration_desc", label: "Longest first" },
  { value: "duration_asc", label: "Shortest first" },
];

function setParam(
  params: URLSearchParams,
  key: string,
  value: string | number | null,
): URLSearchParams {
  const next = new URLSearchParams(params);
  if (value === null || value === undefined || value === "") next.delete(key);
  else next.set(key, String(value));
  next.delete("page");
  return next;
}

export function FlightsListPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const canAccessDirectory = canReviewAllFlights(user?.account_type);

  const queryParams = useMemo(
    () => ({
      search: params.get("search") ?? "",
      status: params.get("status") ?? "",
      mission: params.get("mission") ?? "",
      sort: params.get("sort") ?? "newest",
      user_id: params.get("user_id") ?? "",
      page: Number(params.get("page") ?? 1),
    }),
    [params],
  );

  const { data, isPending, isFetching, isError, error } = useQuery({
    queryKey: ["flights", "table", queryParams],
    queryFn: () =>
      getFlightsTable({
        search: queryParams.search,
        status: queryParams.status,
        mission: queryParams.mission,
        sort: queryParams.sort,
        user_id: queryParams.user_id ? Number(queryParams.user_id) : undefined,
        page: queryParams.page,
      }),
    placeholderData: keepPreviousData,
  });

  const filterKey = JSON.stringify(queryParams);
  const pulse = usePulseOnChange(filterKey, 500);
  const fetchVisible = useDelayedFlag(isFetching && !isPending, 500);
  const showRefetching = !isPending && (pulse || fetchVisible);

  if (isPending) return <p className="empty-state">Loading…</p>;
  if (isError) {
    return (
      <div className="callout danger">
        <strong>Could not load flights</strong>
        <p>{error instanceof Error ? error.message : "Unknown error"}</p>
      </div>
    );
  }
  if (!data) return null;

  const { rows, missions, statuses, users, summary, filters, pagination } = data;

  function updateFilter(key: string, value: string | number | null) {
    setParams(setParam(params, key, value));
  }

  function gotoPage(page: number) {
    const next = new URLSearchParams(params);
    next.set("page", String(page));
    setParams(next);
  }

  return (
    <>
      <PageHeader
        title="Flights"
        lede="Browse imported replays, filter them, and open any flight to review its metrics."
        actions={
          <div className="stat-chips">
            <span className="stat-chip">
              <span className="stat-chip__label">Visible</span>
              <strong>{summary.flights}</strong>
            </span>
            <span className="stat-chip">
              <span className="stat-chip__label">Imports</span>
              <strong>{summary.imports}</strong>
            </span>
            <span className="stat-chip">
              <span className="stat-chip__label">Ready</span>
              <strong>{summary.ready_flights}</strong>
            </span>
            <span className="stat-chip">
              <span className="stat-chip__label">Total time</span>
              <strong>{formatDuration(summary.time_in_air)}</strong>
            </span>
          </div>
        }
      />

      <FilterBar
        filters={buildFilters({
          filters,
          statuses,
          missions,
          users,
          canAccessDirectory,
        })}
        onChange={(key, value) => updateFilter(key, value || null)}
        onClear={() => setParams(new URLSearchParams())}
      />

      <section
        className={`panel${showRefetching ? " is-refetching" : ""}`}
        aria-busy={showRefetching}
      >
        <div className="panel-head">
          <h2>Flights</h2>
          {showRefetching && <span className="panel-refetch">Updating…</span>}
        </div>
        {rows.length ? (
          <>
            <FlightTable
              rows={rows}
              columns={["session", "date", "duration", "distance", "avg_speed", "smoothness", "status"]}
              isInstructor={canAccessDirectory}
            />
            <Pagination
              page={pagination.page}
              pageCount={Math.max(pagination.page_count, 1)}
              total={pagination.total}
              itemLabel="flights"
              onChange={gotoPage}
            />
          </>
        ) : (
          <p className="empty-state">No flights match these filters.</p>
        )}
      </section>
    </>
  );
}

interface BuildFiltersArgs {
  filters: {
    search: string;
    status: string;
    mission: string;
    sort: string;
    user_id: string | number | null;
  };
  statuses: string[];
  missions: string[];
  users: { id: number; username: string; flight_count: number }[];
  canAccessDirectory: boolean;
}

function buildFilters({
  filters,
  statuses,
  missions,
  users,
  canAccessDirectory,
}: BuildFiltersArgs): FilterSpec[] {
  const list: FilterSpec[] = [
    {
      type: "search",
      key: "search",
      placeholder: "Session, map, mission…",
      value: filters.search,
      wide: true,
    },
    {
      type: "select",
      key: "status",
      label: "Status",
      value: filters.status,
      options: [
        { value: "", label: "All" },
        ...statuses.map((s) => ({ value: s, label: s })),
      ],
    },
    {
      type: "select",
      key: "mission",
      label: "Mission",
      value: filters.mission,
      options: [
        { value: "", label: "All" },
        ...missions.map((m) => ({ value: m, label: m })),
      ],
    },
    {
      type: "select",
      key: "sort",
      label: "Sort",
      value: filters.sort,
      options: SORT_OPTIONS,
    },
  ];
  if (canAccessDirectory) {
    list.push({
      type: "select",
      key: "user_id",
      label: "Pilot",
      value: String(filters.user_id ?? ""),
      options: [
        { value: "", label: "All pilots" },
        ...users.map((u) => ({
          value: String(u.id),
          label: `${u.username} (${u.flight_count})`,
        })),
      ],
    });
  }
  return list;
}
